import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Textarea } from 'primeng/textarea';
import { Select } from 'primeng/select';
import { Tag } from 'primeng/tag';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { Message } from 'primeng/message';
import { RealmService } from '@/app/services/realm.service';
import { UserService } from '@/app/services/user.service';
import { AdminUser, AdminWallet, KycStatus, RealmSummary } from '@/lib-backend';

type WalletMode = 'topup' | 'debit';
type KycAction = 'approve' | 'reject' | 'reset';

@Component({
    selector: 'app-users',
    standalone: true,
    imports: [
        DatePipe, FormsModule,
        Button, Dialog, InputText, InputNumber, Textarea, Select, Tag, Toast, Tooltip, Message,
    ],
    providers: [MessageService],
    templateUrl: './users.html',
    styleUrl: './users.scss',
})
export class Users implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly realmService = inject(RealmService);
    private readonly userService = inject(UserService);
    private readonly messageService = inject(MessageService);

    private urlHydrated = false;

    protected readonly selectedRealmId = signal<string | null>(null);
    protected readonly userIdInput = signal('');

    protected readonly looking = signal(false);
    protected readonly lookupError = signal<string | null>(null);

    protected readonly user = signal<AdminUser | null>(null);
    protected readonly kycStatus = signal<KycStatus | null>(null);
    protected readonly kycLoading = signal(false);

    protected readonly realmOptions = computed(() =>
        this.realmService.realms()
            .filter(r => r.IsActive)
            .map(r => ({ label: `${r.DisplayName} — ${r.Domain}`, value: r.RealmId } satisfies RealmOption))
            .sort((a, b) => a.label.localeCompare(b.label))
    );

    protected readonly selectedRealm = computed(() =>
        this.realmService.realms().find(r => r.RealmId === this.selectedRealmId()) ?? null
    );

    protected readonly canLookup = computed(() =>
        !!this.selectedRealmId() && this.userIdInput().trim().length > 0 && !this.looking()
    );

    // ── Wallet dialog ───────────────────────────────────────────────────────
    protected readonly walletDialogVisible = signal(false);
    protected readonly walletMode = signal<WalletMode>('topup');
    protected readonly walletFree = signal<number>(0);
    protected readonly walletPaid = signal<number>(0);
    protected readonly walletReason = signal('');
    protected readonly walletSaving = signal(false);
    protected readonly walletError = signal<string | null>(null);

    // ── KYC action dialog ───────────────────────────────────────────────────
    protected readonly kycDialogVisible = signal(false);
    protected readonly kycAction = signal<KycAction>('approve');
    protected readonly kycInternalReason = signal('');
    protected readonly kycUserReason = signal('');
    protected readonly kycSaving = signal(false);
    protected readonly kycError = signal<string | null>(null);

    ngOnInit(): void {
        this.realmService.loadRealms();

        this.route.queryParamMap
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(params => {
                if (this.urlHydrated) return;
                const realmId = params.get('realmId');
                if (realmId) this.selectedRealmId.set(realmId);
                this.urlHydrated = true;
            });
    }

    protected onRealmChange(realmId: string | null): void {
        this.selectedRealmId.set(realmId);
        // Clear current user — old result no longer applies to a different realm.
        this.user.set(null);
        this.kycStatus.set(null);
        this.lookupError.set(null);

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { realmId: realmId ?? null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    protected async lookup(): Promise<void> {
        const realmId = this.selectedRealmId();
        const userId = this.userIdInput().trim();
        if (!realmId || !userId) return;

        this.looking.set(true);
        this.lookupError.set(null);
        this.user.set(null);
        this.kycStatus.set(null);

        try {
            const userResult = await this.userService.getUser(userId, realmId);
            if (userResult.kind === 'notFound') {
                this.lookupError.set('User not found in this website.');
                return;
            }
            if (userResult.kind !== 'ok') {
                this.lookupError.set('Failed to load user.');
                return;
            }
            this.user.set(userResult.user);
            await this.loadKyc();
        } finally {
            this.looking.set(false);
        }
    }

    private async loadKyc(): Promise<void> {
        const u = this.user();
        if (!u) return;
        this.kycLoading.set(true);
        try {
            const kycResult = await this.userService.getKycStatus(u.Id, u.Realm);
            if (kycResult.kind === 'ok') {
                this.kycStatus.set(kycResult.status);
            } else if (kycResult.kind === 'noStatusYet') {
                this.kycStatus.set({ _type: 'not_started' });
            } else {
                this.kycStatus.set(null);
            }
        } finally {
            this.kycLoading.set(false);
        }
    }

    protected copyUserId(): void {
        const u = this.user();
        if (!u) return;
        navigator.clipboard.writeText(u.Id).then(() => {
            this.messageService.add({
                severity: 'success', summary: 'Copied', detail: 'User ID copied to clipboard.', life: 2000,
            });
        });
    }

    // ── Wallet dialog ───────────────────────────────────────────────────────

    protected openWalletDialog(mode: WalletMode): void {
        this.walletMode.set(mode);
        this.walletFree.set(0);
        this.walletPaid.set(0);
        this.walletReason.set('');
        this.walletError.set(null);
        this.walletDialogVisible.set(true);
    }

    protected closeWalletDialog(): void {
        this.walletDialogVisible.set(false);
    }

    protected async confirmWallet(): Promise<void> {
        const u = this.user();
        if (!u) return;

        const reason = this.walletReason().trim();
        if (!reason) {
            this.walletError.set('Reason is required.');
            return;
        }
        const free = this.walletFree() || 0;
        const paid = this.walletPaid() || 0;
        if (free === 0 && paid === 0) {
            this.walletError.set('Enter a non-zero Free or Paid amount.');
            return;
        }

        // Debit applies negative deltas — flip sign on positive inputs.
        const sign = this.walletMode() === 'debit' ? -1 : 1;
        const signedFree = sign * Math.abs(free);
        const signedPaid = sign * Math.abs(paid);

        this.walletSaving.set(true);
        this.walletError.set(null);
        try {
            const outcome = await this.userService.updateWallet({
                userId: u.Id,
                realmId: u.Realm,
                freeCredits: signedFree,
                paidCredits: signedPaid,
                reason,
                idempotentId: crypto.randomUUID(),
            });

            if (outcome.kind === 'ok') {
                this.applyWallet(outcome.wallet);
                this.walletDialogVisible.set(false);
                this.messageService.add({
                    severity: 'success',
                    summary: this.walletMode() === 'topup' ? 'Topped up' : 'Debited',
                    detail: outcome.wasAlreadyCompleted
                        ? 'Already applied (idempotent retry).'
                        : `Free: ${signedFree}, Paid: ${signedPaid}.`,
                });
            } else if (outcome.kind === 'insufficientFunds') {
                this.walletError.set('Insufficient funds for this debit.');
            } else {
                this.walletError.set('Failed to update wallet.');
            }
        } finally {
            this.walletSaving.set(false);
        }
    }

    private applyWallet(wallet: AdminWallet): void {
        const u = this.user();
        if (!u) return;
        this.user.set({ ...u, Wallet: wallet });
    }

    // ── KYC actions ─────────────────────────────────────────────────────────

    protected openKycDialog(action: KycAction): void {
        this.kycAction.set(action);
        this.kycInternalReason.set('');
        this.kycUserReason.set('');
        this.kycError.set(null);
        this.kycDialogVisible.set(true);
    }

    protected closeKycDialog(): void {
        this.kycDialogVisible.set(false);
    }

    protected copyInternalToUserReason(): void {
        this.kycUserReason.set(this.kycInternalReason());
    }

    protected kycDialogHeader(): string {
        switch (this.kycAction()) {
            case 'approve': return 'Approve KYC';
            case 'reject':  return 'Reject KYC';
            case 'reset':   return 'Reset KYC to pending';
        }
    }

    protected kycConfirmLabel(): string {
        switch (this.kycAction()) {
            case 'approve': return 'Approve';
            case 'reject':  return 'Reject';
            case 'reset':   return 'Reset';
        }
    }

    protected kycConfirmSeverity(): 'success' | 'danger' | 'secondary' {
        switch (this.kycAction()) {
            case 'approve': return 'success';
            case 'reject':  return 'danger';
            case 'reset':   return 'secondary';
        }
    }

    protected async confirmKyc(): Promise<void> {
        const u = this.user();
        if (!u) return;

        const internalReason = this.kycInternalReason().trim();
        if (!internalReason) {
            this.kycError.set('Reason is required.');
            return;
        }

        let newStatus: KycStatus;
        switch (this.kycAction()) {
            case 'approve':
                newStatus = { _type: 'approved' };
                break;
            case 'reset':
                newStatus = { _type: 'pending' };
                break;
            case 'reject': {
                const userReason = this.kycUserReason().trim();
                if (!userReason) {
                    this.kycError.set('Reason shown to the user is required for Reject.');
                    return;
                }
                newStatus = { _type: 'rejected', Reason: userReason };
                break;
            }
        }

        this.kycSaving.set(true);
        this.kycError.set(null);
        try {
            const outcome = await this.userService.updateKycStatus(u.Id, u.Realm, newStatus, internalReason);
            if (outcome.kind === 'ok') {
                this.kycStatus.set(outcome.status);
                this.kycDialogVisible.set(false);
                this.messageService.add({
                    severity: 'success',
                    summary: 'KYC updated',
                    detail: `Status: ${this.kycStatusLabel(outcome.status)}.`,
                });
            } else if (outcome.kind === 'notFound') {
                this.kycError.set('User not found.');
            } else {
                this.kycError.set('Failed to update KYC status.');
            }
        } finally {
            this.kycSaving.set(false);
        }
    }

    // ── Display helpers ─────────────────────────────────────────────────────

    protected kycStatusLabel(status: KycStatus | null): string {
        if (!status) return '—';
        switch (status._type) {
            case 'approved':    return 'Approved';
            case 'not_started': return 'Not started';
            case 'pending':     return 'Pending';
            case 'rejected':    return 'Rejected';
        }
    }

    protected kycSeverity(status: KycStatus | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (!status) return 'secondary';
        switch (status._type) {
            case 'approved':    return 'success';
            case 'pending':     return 'warn';
            case 'rejected':    return 'danger';
            case 'not_started': return 'secondary';
        }
    }

    protected trackByRealmId(_index: number, item: RealmSummary): string {
        return item.RealmId;
    }
}

interface RealmOption {
    label: string;
    value: string;
}
